// src/screens/LoginScreen.js
import { Alert, Button, Link, TextField } from "@mui/material";
import { blueGrey } from "@mui/material/colors";
import { useStytch, useStytchSession } from "@stytch/react";
import { useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import useLocalStorageState from "use-local-storage-state";

import { isNil } from "lodash";
import AnimatedCheckmark from "../components/feedback/AnimatedCheckmark/AnimatedCheckMark";
import InputWrapper from "../components/inputs/shared/InputWrapper";
import Flx from "../components/layout/Flx";
import ScreenLayoutFormCard from "../components/layout/ScreenLayoutFormCard";
import Txt from "../components/typography/Txt";

const LoginScreen = () => {
  // if they're already in, send them home
  const { session } = useStytchSession();
  const stytch = useStytch();

  const [lsEmail, setLsEmail] = useLocalStorageState(
    "borrowerPortalLoginEmail",
    {
      defaultValue: null,
    },
  );
  const [email, setEmail] = useState(lsEmail);
  const navigate = useNavigate();
  const [status, setStatus] = useState("idle"); // "idle", "sending", "sent", "error"
  const [error, setError] = useState(null);
  const [remember, setRemember] = useState(isNil(lsEmail) ? false : true);

  const onRememberChange = (e) => {
    const value = e.target.checked;

    setRemember(value);
    if (!value) {
      setLsEmail(null);
    }
  };

  if (session) {
    return <Navigate to="/" replace />;
  }

  const handleSendMagicLink = async () => {
    setStatus("sending");
    if (remember) {
      setLsEmail(email);
    }

    try {
      await stytch.magicLinks.email.send(email.trim(), {
        login_magic_link_url: `${window.location.origin}/authenticate`,
        login_expiration_minutes: 10080,
        session_duration_minutes: 10080,

        // signup_magic_link_url: `${window.location.origin}/authenticate`,
        // signup_expiration_minutes: 10080,
      });
      // resp.user_created will be true if a new account was created
      setStatus("sent");
      setError(null);
    } catch (err) {
      setError(err?.error_type);
      setStatus("idle");
    }
  };

  if (status === "sent") {
    return <Success email={email} />;
  }

  return (
    <ScreenLayoutFormCard
      title={"Borrower Portal Log In"}
      description={"Please enter your email address below to login."}
      cardWidth={460}
      // cardWidth={510}
    >
      {error && (
        <Alert
          severity="error"
          sx={{ mb: 3, width: "100%" }}
          onClose={() => setError("")}
        >
          {error === "email_not_found"
            ? "We could not find an account associated with that email address. Please try again or create a new account."
            : "Oops, something went wrong. Please try again."}
        </Alert>
      )}

      <InputWrapper sx={{ mb: 2, width: "100%" }}>
        <TextField
          // label="Email address"
          placeholder="Enter your email address..."
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          fullWidth
          disabled={status === "sending" || status === "sent"}
          sx={{
            mb: 1,
            //
            // mt: 2,
          }}
        />
      </InputWrapper>

      <Button
        variant="contained"
        fullWidth
        size="large"
        loading={status === "sending"}
        onClick={handleSendMagicLink}
        disabled={
          !email || status === "sending" || status === "sent" || isNil(email)
        }
      >
        Send Magic Link
      </Button>

      <Flx
        center
        column
        gap={0.1}
        sx={{
          left: 0,
          mt: 3,
          // mt: 6,
          width: "100%",
          background: "#ffffff",
        }}
      >
        <Txt>New to {process.env.REACT_APP_ORGANIZATION_NAME || "us"}?</Txt>
        {/* <Txt>New to Handsome Homebuyer Financing?</Txt> */}

        <Link
          onClick={() => navigate("/sign-up")}
          // onClick={() => navigate("/sign-up")}
          sx={{ textDecoration: "none", cursor: "pointer" }}
        >
          <Txt> Create an account</Txt>
        </Link>
      </Flx>
    </ScreenLayoutFormCard>
  );
};

const Success = ({ email }) => {
  return (
    <ScreenLayoutFormCard title={"Log In"} cardWidth={510}>
      <Flx fw column center g={2}>
        {/* <Flx fw center wrap gap={0.5}>
          <Txt sx={{ color: "#111111" }}>Magic Link Sent</Txt>
        </Flx> */}
        <AnimatedCheckmark size={"64px !important"} sx={{ padding: 0 }} />
        <Flx fw wrap center g={0.5} sx={{ mt: 1 }}>
          <Txt sx={{ color: "#111111", fontSize: "12px" }}>
            Magic Link sent to
          </Txt>
          <Txt
            sx={{
              color: "#111111",
              background: blueGrey[50],
              px: 0.6,
              py: 0.1,
              fontSize: "12px",
            }}
          >
            {email}
          </Txt>
        </Flx>
        <Flx center g={1}>
          <OutlookSvg />
          <Link
            underline="none"
            href="https://outlook.office.com/mail/0/inbox"
            target="_blank"
            rel="noopener noreferrer"
            sx={{
              fontSize: "12px",
              //
              fontFamily: "var(--ibm)",
              color: "#0072c6",
            }}
          >
            Open in Outlook
          </Link>
        </Flx>
      </Flx>
    </ScreenLayoutFormCard>
  );
};

const OutlookSvg = ({ props }) => {
  return (
    <>
      <svg
        xmlns="http://www.w3.org/2000/svg"
        version="1.1"
        id="Layer_1"
        style={{ width: "16px" }}
        viewBox="0 0 103.17322 104.31332"
        xmlSpace="preserve"
      >
        <defs id="defs43"></defs>
        <path
          d="m 64.566509,22.116383 v 20.404273 l 7.130526,4.489881 c 0.188058,0.05485 0.595516,0.05877 0.783574,0 L 103.16929,26.320259 c 0,-2.44867 -2.28412,-4.203876 -3.573094,-4.203876 H 64.566509 z"
          id="path3"
          style={{ fill: "rgb(0, 114, 198)" }}
        ></path>
        <path
          d="m 64.566509,50.13308 6.507584,4.470291 c 0.916782,0.673874 2.021622,0 2.021622,0 -1.100922,0.673874 30.077495,-20.035993 30.077495,-20.035993 v 37.501863 c 0,4.082422 -2.61322,5.794531 -5.551621,5.794531 H 64.562591 V 50.13308 z"
          id="path5"
          style={{ fill: "rgb(0, 114, 198)" }}
        ></path>
        <g
          id="g23"
          transform="matrix(3.9178712,0,0,3.9178712,-13.481403,-41.384473)"
        >
          <path
            d="m 11.321,20.958 c -0.566,0 -1.017,0.266 -1.35,0.797 -0.333,0.531 -0.5,1.234 -0.5,2.109 0,0.888 0.167,1.59 0.5,2.106 0.333,0.517 0.77,0.774 1.31,0.774 0.557,0 0.999,-0.251 1.325,-0.753 0.326,-0.502 0.49,-1.199 0.49,-2.09 0,-0.929 -0.158,-1.652 -0.475,-2.169 -0.317,-0.516 -0.75,-0.774 -1.3,-0.774 z"
            id="path25"
            style={{ fill: "rgb(0, 114, 198)" }}
          ></path>
          <path
            d="m 3.441,13.563 v 20.375 l 15.5,3.25 V 10.563 l -15.5,3 z m 10.372,13.632 c -0.655,0.862 -1.509,1.294 -2.563,1.294 -1.027,0 -1.863,-0.418 -2.51,-1.253 C 8.094,26.4 7.77,25.312 7.77,23.97 c 0,-1.417 0.328,-2.563 0.985,-3.438 0.657,-0.875 1.527,-1.313 2.61,-1.313 1.023,0 1.851,0.418 2.482,1.256 0.632,0.838 0.948,1.942 0.948,3.313 10e-4,1.409 -0.327,2.545 -0.982,3.407 z"
            id="path27"
            style={{ fill: "rgb(0, 114, 198)" }}
          ></path>
        </g>
      </svg>
    </>
  );
};
export default LoginScreen;
